import {
	Lock,
	Database,
	RefreshCw,
	Settings,
	Shield,
	Link,
	Globe,
	UserCheck,
	CheckCircle,
	Cookie,
	Phone,
	Building2,
	Mail,
	MapPin,
	ExternalLink,
	Ticket,
	ChevronRight,
} from "lucide-react";

const SectionHeader = ({ num, icon: Icon, title }) => (
	<div className="flex items-center gap-4 mb-8">
		<div className="w-12 h-12 rounded-lg bg-gradient-to-r from-purple-600 to-blue-600 flex items-center justify-center text-white">
			<Icon size={22} />
		</div>
		<div>
			<div className="text-purple-400 font-mono ">
				SECTION {String(num).padStart(2, "0")}
			</div>
			<h2 className="text-3xl font-bold text-white">{title}</h2>
		</div>
	</div>
);

export default function PrivacyPolicy() {
	return (
		<div className="min-h-screen bg-slate-950 text-slate-100 relative overflow-hidden">
			{/* Subtle grid background */}
			<div className="fixed inset-0 opacity-10">
				<div
					className="absolute inset-0"
					style={{
						backgroundImage: `
              linear-gradient(to right, rgba(148, 163, 184, 0.15) 1px, transparent 1px),
              linear-gradient(to bottom, rgba(148, 163, 184, 0.15) 1px, transparent 1px)
            `,
						backgroundSize: "50px 50px",
					}}
				/>
			</div>

			<div className="container px-6 sm:px-12 py-20 relative z-10">
				{/* Header */}
				{/* <header className="mb-24">
					<div className="flex items-center gap-4 text-slate-400 font-mono text-lg flex-wrap">
						<span>Last updated: [Date]</span>
					</div>
					<div className="mt-8 h-px bg-gradient-to-r from-purple-600 via-blue-600 to-transparent" />
				</header> */}

				{/* 1. Introduction */}
				<section className="mb-20">
					<SectionHeader num={1} icon={Lock} title="Introduction" />
					<div className="space-y-6 text-slate-300 leading-relaxed">
						<p className="text-lg">
							SAN micron ("we", "our", "us") is committed to protecting the privacy of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website{" "}
							<span className="text-purple-400">sanmicron.com</span> (the "Website") or utilise our IT solutions and services.
						</p>
						<p>
							We are an Australian company bound by the Australian Privacy Principles (APPs) under the Privacy Act 1988 (Cth). As we provide services globally, we also respect international data protection laws, including the General Data Protection Regulation (GDPR) for individuals in the European Economic Area (EEA).
						</p>
					</div>
				</section>

				{/* 2. What We Collect */}
				<section className="mb-20">
					<SectionHeader num={2} icon={Database} title="What Information We Collect" />
					<p className="text-slate-300 leading-relaxed mb-6">
						We may collect personal information from you in various ways, including when you visit our site, register on the site, place an order, subscribe to a newsletter, fill out a form (such as our "Support" or "Contact" forms), or in connection with other activities, services, features, or resources we make available. The types of personal information we may collect include:
					</p>
					<div className="grid md:grid-cols-2 gap-6">
						{[
							{
								title: "Identity Data",
								items: ["Name, username or similar identifier", "Job title", "Company name"],
							},
							{
								title: "Contact Data",
								items: ["Email address", "Phone number", "Business address"],
							},
							{
								title: "Support Request Data",
								items: ["Subject and description of your issue", "Selected priority level"],
							},
							{
								title: "Technical Data",
								items: [
									"Internet protocol (IP) address and login data",
									"Browser type, version, and plug-in types",
									"Time zone setting and location",
									"Operating system and platform",
								],
							},
							{
								title: "Usage Data",
								items: ["Information about how you use our Website, products, and services"],
							},
							{
								title: "Marketing & Communications Data",
								items: ["Your preferences in receiving marketing from us", "Your communication preferences"],
							},
						].map((card, index) => (
							<div
								key={index}
								className="h-full rounded-2xl bg-gradient-to-br from-purple-600/5 to-blue-600/5 border border-purple-500/20 p-6 hover:border-purple-500/50 hover:bg-gradient-to-br hover:from-purple-600/10 hover:to-blue-600/10 transition-all duration-300"
							>
								<h3 className="text-lg font-semibold text-white mb-4">{card.title}</h3>
								<ul className="space-y-3 text-slate-300">
									{card.items.map((item, i) => (
										<li key={i} className="flex items-start gap-2">
											<ChevronRight size={16} className="text-purple-400 mt-0.5 flex-shrink-0" />
											<span>{item}</span>
										</li>
									))}
								</ul>
							</div>
						))}
					</div>
				</section>

				{/* 3. How We Collect */}
				<section className="mb-20">
					<SectionHeader num={3} icon={RefreshCw} title="How We Collect Your Information" />
					<div className="space-y-4">
						{[
							{
								title: "Direct Interactions",
								desc: "You may give us your Identity, Contact, and Support Request Data by filling in forms on our Website (like the Support form or Contact form) or by corresponding with us by phone, email, or otherwise.",
							},
							{
								title: "Automated Technologies or Interactions",
								desc: "As you interact with our Website, we may automatically collect Technical Data about your equipment, browsing actions, and patterns. We collect this data by using cookies, server logs, and other similar technologies.",
							},
							{
								title: "Third Parties or Publicly Available Sources",
								desc: "We may receive personal data about you from various third parties, such as analytics providers like Google, and our business affiliates listed on our site (e.g., Google, Microsoft, Amazon Cloud).",
							},
						].map((item, index) => (
							<div
								key={index}
								className="h-full rounded-2xl bg-gradient-to-br from-purple-600/5 to-blue-600/5 border border-purple-500/20 p-5 hover:border-purple-500/50 hover:from-purple-600/10 hover:to-blue-600/10 transition-all duration-300 flex items-start gap-4"
							>
								<div className="w-10 h-10 rounded-lg bg-gradient-to-r from-purple-600 to-blue-600 flex items-center justify-center font-mono text-white flex-shrink-0">
									{String(index + 1).padStart(2, "0")}
								</div>
								<div>
									<h4 className="font-semibold text-white mb-2 text-lg">{item.title}</h4>
									<p className="text-slate-400 ">{item.desc}</p>
								</div>
							</div>
						))}
					</div>
				</section>

				{/* 4. How We Use */}
				<section className="mb-20">
					<SectionHeader num={4} icon={Settings} title="How We Use Your Information" />
					<div className="space-y-4">
						{[
							"To provide and manage your access to our Website.",
							"To deliver the services you have requested, including IT solutions and support. This specifically includes using the information from our Support form to respond to and resolve your technical issues.",
							"To manage our relationship with you, including notifying you about changes to our terms or privacy policy.",
							"To administer and protect our business and this Website (including troubleshooting, data analysis, testing, system maintenance, support, reporting, and hosting of data).",
							"To improve our Website, products, services, marketing, and customer relationships.",
							"To send you relevant marketing communications, where you have not objected to receiving them.",
						].map((text, index) => (
							<div
								key={index}
								className="h-full rounded-2xl bg-gradient-to-br from-purple-600/5 to-blue-600/5 border border-purple-500/20 p-5 hover:border-purple-500/50 hover:from-purple-600/10 hover:to-blue-600/10 transition-all duration-300 flex items-center gap-4"
							>
								<div className="w-10 h-10 rounded-lg bg-gradient-to-r from-purple-600 to-blue-600 flex items-center justify-center font-mono text-white flex-shrink-0">
									{String(index + 1).padStart(2, "0")}
								</div>
								<p className="text-slate-300 leading-relaxed">{text}</p>
							</div>
						))}
					</div>
				</section>

				{/* 5. Disclosure */}
				<section className="mb-20">
					<SectionHeader num={5} icon={Link} title="Disclosure of Your Information" />
					<p className="text-slate-300 leading-relaxed mb-6">We may share your information with:</p>
					<div className="grid sm:grid-cols-3 gap-4">
						{[
							{
								title: "Service Providers",
								desc: "Third-party vendors who provide services on our behalf, including Digital Ocean, Amazon Cloud (AWS), Azure, and N-able. Contractually obligated to protect your information.",
							},
							{
								title: "Professional Advisers",
								desc: "Lawyers, bankers, auditors, and insurers who provide consultancy, banking, legal, insurance, and accounting services.",
							},
							{
								title: "Regulatory & Law Enforcement",
								desc: "Where required by law, or to protect the rights, property, or safety of SAN micron, our customers, or others.",
							},
						].map((item, index) => (
							<div
								key={index}
								className="h-full rounded-2xl bg-gradient-to-br from-purple-600/5 to-blue-600/5 border border-purple-500/20 p-5 hover:border-purple-500/50 hover:from-purple-600/10 hover:to-blue-600/10 transition-all duration-300"
							>
								<h4 className="font-semibold text-white mb-2 text-lg">{item.title}</h4>
								<p className="text-slate-400">{item.desc}</p>
							</div>
						))}
					</div>
				</section>

				{/* 6. International Transfers */}
				<section className="mb-20">
					<SectionHeader num={6} icon={Globe} title="International Data Transfers" />
					<div className="h-full rounded-2xl bg-gradient-to-br from-purple-600/5 to-blue-600/5 border border-purple-500/20 p-6 hover:border-purple-500/50 hover:from-purple-600/10 hover:to-blue-600/10 transition-all duration-300 text-slate-300 leading-relaxed">
						<p>
							As a global company, we may transfer your personal information to recipients in countries outside Australia, including countries that may not have the same level of data protection. This is inherent in our use of global partners and our service to international clients. When we do so, we will take reasonable steps to ensure that any such international transfers of information are managed to protect your rights and interests, such as by using standard contractual clauses approved by relevant authorities (for GDPR purposes) or other lawful transfer mechanisms.
						</p>
					</div>
				</section>

				{/* 7. Data Security */}
				<section className="mb-20">
					<SectionHeader num={7} icon={Shield} title="Data Security" />
					<div className="h-full rounded-2xl bg-gradient-to-br from-purple-600/5 to-blue-600/5 border border-purple-500/20 p-6 hover:border-purple-500/50 hover:from-purple-600/10 hover:to-blue-600/10 transition-all duration-300 text-slate-300 leading-relaxed">
						<p>
							We have implemented appropriate technical and organisational security measures designed to protect your personal information from accidental loss, unauthorised access, use, alteration, or disclosure. However, please note that no method of transmission over the Internet or method of electronic storage is 100% secure.
						</p>
					</div>
				</section>

				{/* 8. Data Retention */}
				<section className="mb-20">
					<SectionHeader num={8} icon={Database} title="Data Retention" />
					<div className="h-full rounded-2xl bg-gradient-to-br from-purple-600/5 to-blue-600/5 border border-purple-500/20 p-6 hover:border-purple-500/50 hover:from-purple-600/10 hover:to-blue-600/10 transition-all duration-300 text-slate-300 leading-relaxed">
						<p>
							We will only retain your personal information for as long as reasonably necessary to fulfil the purposes we collected it for, including for the purposes of satisfying any legal, regulatory, tax, accounting, or reporting requirements.
						</p>
					</div>
				</section>

				{/* 9. Your Legal Rights */}
				<section className="mb-20">
					<SectionHeader num={9} icon={UserCheck} title="Your Legal Rights" />
					<div className="rounded-2xl bg-gradient-to-br from-purple-600/5 to-blue-600/5 border border-purple-500/20 p-8 hover:border-purple-500/50 hover:from-purple-600/10 hover:to-blue-600/10 transition-all duration-300">
						<p className="text-slate-300 mb-8 leading-relaxed">
							Under certain circumstances, you have rights under data protection laws in relation to your personal information. These may include the right to:
						</p>
						<div className="grid md:grid-cols-2 gap-4">
							{[
								{ right: "Request Access", desc: "Request access to your personal information." },
								{ right: "Request Correction", desc: "Request correction of your personal information." },
								{ right: "Request Erasure", desc: "Request erasure of your personal information." },
								{ right: "Object to Processing", desc: "Object to processing of your personal information." },
								{ right: "Request Restriction", desc: "Request restriction of processing your personal information." },
								{ right: "Request Transfer", desc: "Request transfer of your personal information." },
								{ right: "Withdraw Consent", desc: "Right to withdraw consent at any time." },
							].map((item, index) => (
								<div
									key={index}
									className="rounded-2xl bg-gradient-to-br from-purple-600/5 to-blue-600/5 border border-purple-500/20 p-5 hover:border-purple-500/50 transition-all duration-300"
								>
									<div className="flex items-start gap-3 mb-2">
										<div className="w-9 h-9 rounded bg-gradient-to-r from-purple-600 to-blue-600 flex items-center justify-center flex-shrink-0">
											<span className="text-white">{index + 1}</span>
										</div>
										<div>
											<h4 className="font-bold text-white mb-1 text-lg">{item.right}</h4>
											<p className="text-slate-400 ">{item.desc}</p>
										</div>
									</div>
								</div>
							))}
						</div>
						<p className="text-slate-400 mt-6">
							If you wish to exercise any of these rights, please contact us. If you are in the EEA, you also have the right to lodge a complaint with your local data protection supervisory authority.
						</p>
					</div>
				</section>

				{/* 10. Cookies */}
				<section className="mb-20">
					<SectionHeader num={10} icon={Cookie} title="Cookies and Tracking Technologies" />
					<div className="h-full rounded-2xl bg-gradient-to-br from-purple-600/5 to-blue-600/5 border border-purple-500/20 p-6 hover:border-purple-500/50 hover:from-purple-600/10 hover:to-blue-600/10 transition-all duration-300 text-slate-300 leading-relaxed">
						<p>
							Our Website uses cookies to distinguish you from other users, provide a good experience, and improve our site. You can set your browser to refuse all or some browser cookies, but this may affect the functionality of the Website.
						</p>
					</div>
				</section>

				{/* 11. Third-Party Links */}
				<section className="mb-20">
					<SectionHeader num={11} icon={ExternalLink} title="Third-Party Links" />
					<div className="h-full rounded-2xl bg-gradient-to-br from-purple-600/5 to-blue-600/5 border border-purple-500/20 p-6 hover:border-purple-500/50 hover:from-purple-600/10 hover:to-blue-600/10 transition-all duration-300 text-slate-300 leading-relaxed">
						<p>
							Our Website may include links to third-party websites, plug-ins, and applications. Clicking on those links may allow third parties to collect or share data about you. We do not control these third-party websites and are not responsible for their privacy statements.
						</p>
					</div>
				</section>

				{/* 12. Changes */}
				<section className="mb-20">
					<SectionHeader num={12} icon={CheckCircle} title="Changes to This Privacy Policy" />
					<div className="h-full rounded-2xl bg-gradient-to-br from-purple-600/5 to-blue-600/5 border border-purple-500/20 p-6 hover:border-purple-500/50 hover:from-purple-600/10 hover:to-blue-600/10 transition-all duration-300 text-slate-300 leading-relaxed">
						<p>
							We may update this privacy policy from time to time. We will notify you of any changes by posting the new privacy policy on this page with an updated revision date.
						</p>
					</div>
				</section>

				{/* 13. Contact */}
				<section className="mb-20">
					<SectionHeader num={13} icon={Phone} title="Contact Us" />
					<div className="rounded-2xl bg-gradient-to-br from-purple-600/5 to-blue-600/5 border border-purple-500/20 p-8 hover:border-purple-500/50 hover:from-purple-600/10 hover:to-blue-600/10 transition-all duration-300">
						<p className="text-slate-300 mb-6 leading-relaxed">
							If you have any questions about this Privacy Policy or our privacy practices, or if you need to submit a support request, please contact us at:
						</p>
						<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
							{[
								{ Icon: Building2, label: "Company", value: "SAN micron", href: null },
								{ Icon: Phone, label: "Phone", value: "+61 391 167 215", href: "tel:+61391167215" },
								{ Icon: Mail, label: "Email", value: "info@sanmicron.com", href: "mailto:info@sanmicron.com" },
								{ Icon: MapPin, label: "Address", value: "Melbourne, Australia", href: null },
								{ Icon: ExternalLink, label: "Contact Form", value: "sanmicron.com/contact/", href: "https://sanmicron.com/contact/" },
								{ Icon: Ticket, label: "Support Form", value: "sanmicron.com/support/", href: "https://sanmicron.com/support/" },
							].map(({ Icon, label, value, href }, index) => (
								<div
									key={index}
									className="flex items-center gap-4 p-4 rounded-2xl bg-gradient-to-br from-purple-600/5 to-blue-600/5 border border-purple-500/20 hover:border-purple-500/50 transition-all duration-300"
								>
									<div className="w-12 h-12 rounded-lg bg-gradient-to-r from-purple-600 to-blue-600 flex items-center justify-center flex-shrink-0 text-white">
										<Icon size={20} />
									</div>
									<div>
										<div className="text-gray-300 mb-1">{label}</div>
										{href ? (
											<a
												href={href}
												target={href.startsWith("http") ? "_blank" : undefined}
												rel={href.startsWith("http") ? "noopener noreferrer" : undefined}
												className="text-gray-200 hover:text-purple-400 transition-colors duration-200 underline-offset-2 hover:underline"
											>
												{value}
											</a>
										) : (
											<div className="text-gray-200">{value}</div>
										)}
									</div>
								</div>
							))}
						</div>
					</div>
				</section>
			</div>
		</div>
	);
}